import { useTranslation } from "react-i18next";
import { ViewIcon } from "@/components/icons/action-icons";
import { AlarmClock, CircleCheck, CircleXIcon, Clock, FileText, OctagonMinus, RotateCcw } from "lucide-react";
import { formatKababToCamelCase } from "@/utils/helper";
import { Badge } from "@/components/ui/badge";
import type { BadgeVariant } from "@/components/ui/badge/badge.variants";
import { isImageUrl } from "@/context/ImagePreview";

// ── Priority badge ────────────────────────────────────────────────────────────

const PRIORITY_VARIANT: Record<string, BadgeVariant> = {
  urgent: "danger",
  high:   "warning",
  medium: "success",
  low:    "info",
};

export const PriorityBadge = ({ priority }: { priority: string }) => {
  const { t } = useTranslation("common");
  return (
    <Badge
      variant={PRIORITY_VARIANT[priority?.toLowerCase()] ?? "neutral"}
      className="capitalize"
    >
      {t(`common:priority.${priority?.toLowerCase()}`, {
        defaultValue: formatKababToCamelCase(priority ?? ""),
      })}
    </Badge>
  );
};

// ── Status icon ───────────────────────────────────────────────────────────────

export const StatusIcon = ({ status }: { status: string }) => {
  switch (status) {
    case "pending":
      return <Clock className="w-3.5 h-3.5" />;
    case "cancelled":
      return <OctagonMinus className="w-3.5 h-3.5" />;
    case "verified":
    case "approved":
      return <CircleCheck className="w-3.5 h-3.5" />;
    case "rejected":
      return <CircleXIcon className="w-3.5 h-3.5" />;
    case "in_progress":
      return <Clock className="w-3.5 h-3.5" />;
    case "overdue":
      return <AlarmClock className="w-3.5 h-3.5" />;
    case "changes_requested":
      return <RotateCcw className="w-3.5 h-3.5" />;
    default:
      return null;
  }
};

// ── Request ID cell ───────────────────────────────────────────────────────────

export const RequestIdCell = ({ requestId }: { requestId: string }) => (
  <span className="font-mono text-xs text-muted-foreground bg-muted px-2 py-1 rounded-xxs">
    {requestId}
  </span>
);

// ── Required date cell ────────────────────────────────────────────────────────

export const RequiredDateCell = ({ date }: { date: string | null | undefined }) =>
  date ? <>{date}</> : <span className="text-muted-foreground">—</span>;

// ── Reason cell ───────────────────────────────────────────────────────────────

export const ReasonCell = ({ reason }: { reason: string }) => (
  <p
    className="line-clamp-2 max-w-[200px] whitespace-pre-line leading-tight text-sm"
    data-tooltip-id="hrm-tooltip"
    data-tooltip-content={reason}
  >
    {reason}
  </p>
);

// ── Attachment thumbnail cell ──────────────────────────────────────────────────

interface AttachmentCellProps {
  url: string | null | undefined;
  onPreview: () => void;
  noRowClick?: boolean;
}

export const AttachmentCell = ({ url, onPreview, noRowClick = false }: AttachmentCellProps) => {
  const { t } = useTranslation("common");
  if (!url) return <span className="text-muted-foreground text-xs">—</span>;

  return (
    <button
      onClick={onPreview}
      {...(noRowClick ? { "data-no-row-click": true } : {})}
      className="group relative w-14 h-8 rounded-sm overflow-hidden border border-border shrink-0 flex items-center justify-center hover:border-primary transition-colors"
      data-tooltip-id="hrm-tooltip"
      data-tooltip-content={t("shared_ui.preview")}
    >
      {isImageUrl(url) ? (
        <>
          <img src={url} alt={t("shared_ui.attachment")} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
            <ViewIcon className="w-4 h-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
        </>
      ) : (
        <div className="w-full h-full bg-primary/10 flex items-center justify-center">
          <FileText className="w-4 h-4 text-primary" />
        </div>
      )}
    </button>
  );
};
